import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen py-12" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-8 w-8 text-primary" />
            <h1 
              className="text-3xl font-bold"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Privacy Policy
            </h1>
          </div>
          <p className="text-muted-foreground">
            Last updated: January 2026
          </p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          <section className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              1. Information We Collect
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              When you use HabeshaTown, we collect basic information necessary to provide our services:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span><strong>Account Information:</strong> Name, email address, and password when you register.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span><strong>Listing Information:</strong> Details you provide when creating listings, including titles, descriptions, prices, and images.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span><strong>Messages:</strong> Communications between users and with our support team.</span>
              </li>
            </ul>
          </section>

          <section className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              2. How We Use Your Information
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use your information solely for platform functionality:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span>To create and manage your account</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span>To display your listings to other users</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span>To enable communication between buyers and sellers</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span>To provide customer support</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span>To improve our platform and services</span>
              </li>
            </ul>
          </section>

          <section className="bg-primary/5 border border-primary/20 rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              3. We Do NOT Sell Your Data
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              <strong>We do not sell, rent, or share your personal information with third parties for marketing purposes.</strong> Your data is used only to operate HabeshaTown and provide you with our services.
            </p>
          </section>

          <section className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              4. Data Security
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement reasonable security measures to protect your personal information. Passwords are encrypted, and we use secure connections for data transmission. However, no method of electronic storage is 100% secure.
            </p>
          </section>

          <section className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              5. Your Rights
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span>Access your personal information</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span>Update or correct your information</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                <span>Delete your account and associated data</span>
              </li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              To exercise these rights, please contact us through our support system.
            </p>
          </section>

          <section className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              6. Contact Us
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have questions about this Privacy Policy, please contact us through our{' '}
              <Link to="/support" className="text-primary hover:underline">Help & Support</Link> page.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
